import type { NowResume, NowTodayItem } from "../api/now";
import { dueRitual, type RitualLogEntry, type RitualPrefs, type ScheduledRitual } from "./rituals";
import { localDateKey } from "./reflectionDrip";

// The single "For now" suggestion on Today (spec 2026-11-04 §5). Pure: the
// hook gathers data the app already loads, this picks one. No AI call.

export const EVENING_HOUR = 17;
export const LABEL_MAX = 60;
export const MORE_MAX = 3;
export const MODEL_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
export const DISMISS_STORAGE_KEY = "today_for_now_dismissed_v1";

export type ForNowText = { text: string } | { key: string; params?: Record<string, string | number>; dayTs?: number };
export type ForNowLength = { kind: "minutes"; count: number } | { kind: "few" };
export type ForNowAction = "start" | "continue" | "open" | "finish" | "answer";

export interface RunningProcess {
  _id: string;
  status?: string;
  lastUpdated?: number;
  started?: number;
  pendingInnerProcess?: string | null;
  prescriptionName?: string;
  stepStack?: unknown[];
}

export interface ForNowModel {
  _id: string;
  label?: string;
  statement: string;
  status: string;
  visitCount: number;
  createdOn: string;
  context?: { captureId?: string };
}

export interface ForNowTask {
  _id: string;
  content: string;
  type?: string;
  status?: string;
  dueDate?: number | null;
  parentId?: string | null;
  __isPwf?: boolean;
  source?: { distress?: boolean; captureId?: string };
}

interface Base {
  key: string;
  title: ForNowText;
  why: ForNowText | null;
  length: ForNowLength | null;
  action: ForNowAction;
}

export type ForNowCandidate =
  | (Base & { kind: "resume"; resume: NowResume })
  | (Base & { kind: "ritual"; ritual: ScheduledRitual })
  | (Base & { kind: "evening_step"; item: NowTodayItem })
  | (Base & { kind: "activity"; process: RunningProcess })
  | (Base & { kind: "model"; objectId: string })
  | (Base & { kind: "overdue_step"; takeawayId: string })
  | (Base & { kind: "write_first" });

export interface ForNowInputs {
  nowToday: { items: NowTodayItem[]; resume: NowResume | null } | null;
  hasCompass: boolean;
  ritualPrefs: RitualPrefs | null;
  ritualLog: RitualLogEntry[];
  running: RunningProcess[];
  models: ForNowModel[];
  actions: ForNowTask[];
  dismissed: string[];
}

export interface ForNowChoice {
  first: ForNowCandidate;
  more: ForNowCandidate[];
  /** Every remaining candidate after `first` ("N more waiting"); `more` shows up to 3. */
  moreTotal: number;
}

export const WRITE_FIRST: ForNowCandidate = {
  kind: "write_first",
  key: "write_first",
  title: { key: "todayHome.forNow.writeFirst.title" },
  why: { key: "todayHome.forNow.writeFirst.why" },
  length: null,
  action: "start",
};

const RITUAL_MINUTES: Record<ScheduledRitual, number> = { morning: 2, evening: 2, weekly: 10 };

/** Plain text, cut at a word boundary with "…" so the result is at most `max` characters. */
export function shortLabel(raw: string, max = LABEL_MAX): string {
  const clean = String(raw ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > 0 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

const startOfDay = (d: Date): number => {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return s.getTime();
};
const msOf = (v: string | number | undefined): number =>
  typeof v === "number" ? v : v ? new Date(v).getTime() : NaN;

export function chooseForNow(inputs: ForNowInputs, now: Date): ForNowChoice {
  const evening = now.getHours() >= EVENING_HOUR;
  const nowMs = now.getTime();
  const distressIds = new Set(inputs.actions.filter((a) => a.source?.distress).map((a) => a._id));
  const distressCaptures = new Set(
    inputs.actions
      .filter((a) => a.source?.distress && a.source.captureId)
      .map((a) => a.source?.captureId as string)
  );
  const out: ForNowCandidate[] = [];

  // 1. Unfinished hand-off (daytime only).
  const resume = inputs.nowToday?.resume ?? null;
  if (!evening && resume) {
    out.push({
      kind: "resume",
      key: `resume:${resume.sessionId}`,
      resume,
      title: { key: "todayHome.forNow.resume.title" },
      why: { text: shortLabel(resume.note) },
      length: null,
      action: "finish",
    });
  }

  // 2. Due ritual.
  if (inputs.hasCompass) {
    const due = dueRitual(inputs.ritualPrefs, inputs.ritualLog, now);
    if (due) {
      out.push({
        kind: "ritual",
        key: `ritual:${due}`,
        ritual: due,
        title: { key: `todayHome.forNow.ritual.${due}` },
        why: { key: "todayHome.forNow.ritual.why" },
        length: { kind: "minutes", count: RITUAL_MINUTES[due] },
        action: "start",
      });
    }
  }

  // 3. Evening step check (from 17:00).
  const step = evening ? inputs.nowToday?.items?.[0] : undefined;
  if (step && !distressIds.has(step.stepId)) {
    out.push({
      kind: "evening_step",
      key: `evening:${step.sessionId}`,
      item: step,
      title: { key: "todayHome.forNow.evening.title" },
      why: { text: shortLabel(step.stepText) },
      length: null,
      action: "answer",
    });
  }

  // 4. Activity in progress — the most recent running one.
  const latest = inputs.running
    .filter((p) => !p.status || p.status === "RUNNING")
    .sort((a, b) => (b.lastUpdated || b.started || 0) - (a.lastUpdated || a.started || 0))[0];
  if (latest) {
    const name = shortLabel(latest.prescriptionName ?? "");
    const steps = Array.isArray(latest.stepStack) ? latest.stepStack.length : 0;
    const since = latest.lastUpdated || latest.started;
    out.push({
      kind: "activity",
      key: `activity:${latest._id}`,
      process: latest,
      title: name ? { key: "todayHome.forNow.activity.title", params: { name } } : { key: "todayHome.forNow.activity.titleUnnamed" },
      why: steps > 0
        ? { key: "todayHome.continue.step", params: { n: steps } }
        : since
          ? { key: "todayHome.forNow.activity.since", dayTs: since }
          : null,
      length: null,
      action: "continue",
    });
  }

  // 5. Unopened recent models, newest first.
  inputs.models
    .filter(
      (m) =>
        m.status === "active" &&
        m.visitCount === 0 &&
        nowMs - msOf(m.createdOn) <= MODEL_WINDOW_MS &&
        !(m.context?.captureId && distressCaptures.has(m.context.captureId))
    )
    .sort((a, b) => msOf(b.createdOn) - msOf(a.createdOn))
    .forEach((m) =>
      out.push({
        kind: "model",
        key: `model:${m._id}`,
        objectId: m._id,
        title: { key: "todayHome.forNow.model.title", params: { label: shortLabel(m.label || m.statement) } },
        why: { key: "todayHome.forNow.model.why", dayTs: msOf(m.createdOn) },
        length: { kind: "few" },
        action: "open",
      })
    );

  // 6. Overdue steps, most recently due first.
  const sod = startOfDay(now);
  inputs.actions
    .filter(
      (a) =>
        (a.type ?? "action") === "action" &&
        (a.status ?? "pending") === "pending" &&
        !a.parentId &&
        !a.__isPwf &&
        a.dueDate != null &&
        Number(a.dueDate) < sod &&
        !a.source?.distress &&
        a._id !== step?.stepId
    )
    .sort((a, b) => Number(b.dueDate) - Number(a.dueDate))
    .forEach((a) =>
      out.push({
        kind: "overdue_step",
        key: `step:${a._id}`,
        takeawayId: a._id,
        title: { text: shortLabel(a.content) },
        why: { key: "todayHome.forNow.step.why", dayTs: Number(a.dueDate) },
        length: null,
        action: "open",
      })
    );

  const dismissed = new Set(inputs.dismissed);
  const seen = new Set<string>();
  const list = out.filter((c) => {
    if (dismissed.has(c.key) || seen.has(c.key)) return false;
    seen.add(c.key);
    return true;
  });
  if (list.length === 0) return { first: WRITE_FIRST, more: [], moreTotal: 0 };
  return { first: list[0], more: list.slice(1, 1 + MORE_MAX), moreTotal: list.length - 1 };
}

const safeLocal = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Keys dismissed with "Not now" today (local date); anything from another day is ignored. */
export function readDismissed(now: Date, storage: Storage | null = safeLocal()): string[] {
  try {
    const raw = storage?.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    if (!v || v.day !== localDateKey(now) || !Array.isArray(v.keys)) return [];
    return v.keys.filter((k: unknown): k is string => typeof k === "string");
  } catch {
    return [];
  }
}

export function dismissForNow(key: string, now: Date, storage: Storage | null = safeLocal()): void {
  try {
    const keys = readDismissed(now, storage);
    if (!keys.includes(key)) keys.push(key);
    storage?.setItem(DISMISS_STORAGE_KEY, JSON.stringify({ day: localDateKey(now), keys }));
  } catch {
    /* storage blocked: the dismissal lasts this render only */
  }
}

/** Takeaways the For now card already shows — Today's top three must not repeat them. */
export const forNowTakeawayIds = (c: ForNowCandidate): string[] =>
  c.kind === "overdue_step" ? [c.takeawayId] : c.kind === "evening_step" ? [c.item.stepId] : [];
